import { Component, OnInit, Signal, computed, inject, input, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, merge } from 'rxjs';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import {
  ButtonComponent,
  DropdownComponent,
  FieldsetComponent,
  FieldsetLabelDirective,
  FormFieldComponent,
  FormFieldLabelDirective,
  ModalActionsDirective,
  ModalComponent,
  ModalTitleDirective,
  RadioComponent,
  RadioInputDirective,
  RadioLabelDirective,
  TagComponent,
} from '@totalenergiescode/tds-angular';
import { dispatch, select } from '@store/injectables';
import { RegionsViewModel, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import { SourcesViewModel, selectSourcesViewModel } from '@store/scenario/sources/sources-slice';
import { BasecasesViewModel, selectBasecasesViewModel } from '@store/scenario/basecases/basecases-slice';
import { WeatherYearsViewModel, selectWeatherYearsViewModel } from '@store/scenario/weather-years/weather-years-slice';
import { requestHourlyData } from '@store/scenario/hourly-data-request/hourly-data-request-slice';
import { retrieveHourlyRequestFilteredReferentialsIds } from '@store/scenario/hourly-request-filtered-referentials/hourly-request-filtered-referentials-ids-slice';
import {
  hourlyRequestBasecaseLabelSuffixes,
  pmfHourlyRequestBasecaseNames,
} from '@configurations/scenario/hourly-request-basecase-label-suffixes.configuration';
import { PMF_SOURCE } from '@configurations/scenario/source-year-limits.configuration';
import { AuthenticationService } from '../../security/authentication.service';

type HourlyDataReasonType = 'specific' | 'other';
type HourlyDataSpecificReason =
  | 'NEGATIVE_PRICE_HOUR'
  | 'GENERATION_LOSS'
  | 'PRICE_DISTRIBUTION'
  | 'PRICE_VOLATILITY';

const specificReasonLabels: Record<HourlyDataSpecificReason, string> = {
  NEGATIVE_PRICE_HOUR: 'Number of negative price hours',
  GENERATION_LOSS: 'Generation loss during negative hours',
  PRICE_DISTRIBUTION: 'Price distribution',
  PRICE_VOLATILITY: 'Price volatility: one-hour daily spread',
};

@Component({
  selector: 'app-psf-hourly-data-request-modal',
  imports: [
    ButtonComponent,
    DropdownComponent,
    FieldsetComponent,
    FieldsetLabelDirective,
    FormFieldComponent,
    FormFieldLabelDirective,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatSelectTrigger,
    ModalActionsDirective,
    ModalComponent,
    ModalTitleDirective,
    RadioComponent,
    RadioInputDirective,
    RadioLabelDirective,
    ReactiveFormsModule,
    TagComponent,
  ],
  templateUrl: './psf-hourly-data-request-modal.component.html',
  styleUrl: './psf-hourly-data-request-modal.component.scss',
})
export class PsfHourlyDataRequestModalComponent implements OnInit {
  private readonly dispatch = dispatch();
  private readonly authenticationService = inject(AuthenticationService);

  public readonly modal = viewChild.required(ModalComponent);

  defaultZone = input<string>();
  defaultSource = input<string>();
  defaultBasecase = input<string>();
  defaultWeatherYear = input<string>();

  regionsViewModel: Signal<RegionsViewModel> = select(selectRegionsViewModel);
  sourcesViewModel: Signal<SourcesViewModel> = select(selectSourcesViewModel);
  basecasesViewModel: Signal<BasecasesViewModel> = select(selectBasecasesViewModel);
  weatherYearsViewModel: Signal<WeatherYearsViewModel> = select(selectWeatherYearsViewModel);

  filteredZoneIds = select(state => state.hourlyRequestFilteredReferentialsIds.filteredZoneIds);
  filteredSourceIds = select(state => state.hourlyRequestFilteredReferentialsIds.filteredSourceIds);
  filteredBasecaseIds = select(state => state.hourlyRequestFilteredReferentialsIds.filteredBasecaseIds);
  filteredWeatherYearIds = select(state => state.hourlyRequestFilteredReferentialsIds.filteredWeatherYearIds);

  connectedUser = toSignal(this.authenticationService.connectedUser, { initialValue: null });

  regionsOptions = computed(() => {
    const filteredIds = this.filteredZoneIds();
    const regions = this.regionsViewModel().regions;
    const availableRegions = filteredIds.length > 0 ? regions.filter(region => filteredIds.includes(region.id)) : regions;
    return availableRegions.map(region => ({
      value: region.id,
      label: region.name + ' - ' + region.alias,
    }));
  });

  sourcesOptions = computed(() => {
    const filteredIds = this.filteredSourceIds();
    const sources = this.sourcesViewModel().sources;
    const availableSources = filteredIds.length > 0 ? sources.filter(source => filteredIds.includes(source.id)) : sources;
    return availableSources.map(source => ({
      value: source.id,
      label: source.name,
    }));
  });

  basecasesOptions = computed(() => {
    const filteredIds = this.filteredBasecaseIds();
    const basecases = this.basecasesViewModel().basecases;
    const availableBasecases = filteredIds.length > 0 ? basecases.filter(basecase => filteredIds.includes(basecase.id)) : basecases;
    const allowedBasecases = this.isPmfSourceSelected()
      ? availableBasecases.filter(basecase => pmfHourlyRequestBasecaseNames.includes(basecase.name.toLowerCase()))
      : availableBasecases;
    return allowedBasecases.map(basecase => ({
      value: basecase.id,
      label: this.isPmfSourceSelected() ? basecase.name : this.basecaseLabel(basecase.name),
    }));
  });

  private basecaseLabel(name: string): string {
    const suffix = hourlyRequestBasecaseLabelSuffixes[name.toLowerCase()];
    return suffix ? `${name} (${suffix})` : name;
  }

  // The average weather year is not a valid selection for an hourly data request.
  weatherYearsOptions = computed(() => {
    const filteredIds = this.filteredWeatherYearIds();
    const weatherYears = this.weatherYearsViewModel().weatherYears.filter(weatherYear => weatherYear.name !== 'AVERAGE');
    const availableWeatherYears = filteredIds.length > 0 ? weatherYears.filter(weatherYear => filteredIds.includes(weatherYear.id)) : weatherYears;
    return availableWeatherYears.map(weatherYear => ({
      value: weatherYear.id,
      label: weatherYear.name,
    }));
  });

  private readonly selectedSourceId = signal<number | null>(null);

  private readonly isPmfSourceSelected = computed(
    () => this.sourcesViewModel().sources.find(source => source.id === this.selectedSourceId())?.name === PMF_SOURCE,
  );

  isWeatherYearHidden = computed(() => {
    const source = this.sourcesViewModel().sources.find(source => source.id === this.selectedSourceId());
    return source?.name.toUpperCase() === 'AURORA';
  });

  specificReasonOptions = Object.entries(specificReasonLabels).map(([value, label]) => ({
    value,
    label,
  }));

  hourlyDataRequestForm = new FormGroup({
    zone: new FormControl<number | null>(null, Validators.required),
    source: new FormControl<number | null>(null, Validators.required),
    basecase: new FormControl<number | null>(null, Validators.required),
    yearsStart: new FormControl<number | null>(null),
    yearsEnd: new FormControl<number | null>(null),
    reasonType: new FormControl<HourlyDataReasonType>('specific'),
    specificReason: new FormControl<HourlyDataSpecificReason[]>([]),
    weatherYears: new FormControl<number[]>([]),
    comment: new FormControl('', Validators.required),
  });

  constructor() {
    this.updateReasonValidators(this.hourlyDataRequestForm.controls.reasonType.value);
    this.hourlyDataRequestForm.controls.reasonType.valueChanges.subscribe(reasonType => {
      this.updateReasonValidators(reasonType);
      this.resetReasonFields(reasonType);
    });

    this.hourlyDataRequestForm.controls.source.valueChanges.subscribe(sourceId => {
      this.selectedSourceId.set(sourceId);
      if (this.isWeatherYearHidden()) {
        this.hourlyDataRequestForm.controls.weatherYears.setValue([]);
      }
      this.resetBasecaseNotAllowedForSource();
      this.updateReasonValidators(this.hourlyDataRequestForm.controls.reasonType.value);
    });

    merge(
      this.hourlyDataRequestForm.controls.zone.valueChanges,
      this.hourlyDataRequestForm.controls.source.valueChanges,
      this.hourlyDataRequestForm.controls.basecase.valueChanges,
      this.hourlyDataRequestForm.controls.weatherYears.valueChanges,
    )
      .pipe(debounceTime(0))
      .subscribe(() => this.retrieveHourlyRequestDynamicFilters());
  }

  private resetBasecaseNotAllowedForSource(): void {
    const basecaseControl = this.hourlyDataRequestForm.controls.basecase;
    if (!this.isPmfSourceSelected() || basecaseControl.value === null) {
      return;
    }
    const selectedBasecase = this.basecasesViewModel().basecases.find(basecase => basecase.id === basecaseControl.value);
    if (!selectedBasecase || !pmfHourlyRequestBasecaseNames.includes(selectedBasecase.name.toLowerCase())) {
      basecaseControl.setValue(null);
    }
  }

  private retrieveHourlyRequestDynamicFilters(): void {
    const formValue = this.hourlyDataRequestForm.value;
    this.dispatch(
      retrieveHourlyRequestFilteredReferentialsIds({
        inactiveFields: ['zoneIds', 'sourceIds', 'basecaseIds', 'weatherYearIds'],
        filteredReferentialsIds: {
          zoneIds: formValue.zone ? [formValue.zone] : [],
          sourceIds: formValue.source ? [formValue.source] : [],
          basecaseIds: formValue.basecase ? [formValue.basecase] : [],
          weatherYearIds: formValue.weatherYears ?? [],
        },
      }),
    );
  }

  private resetReasonFields(reasonType: HourlyDataReasonType | null): void {
    if (reasonType === 'other') {
      this.hourlyDataRequestForm.controls.specificReason.setValue([]);
    } else {
      this.hourlyDataRequestForm.controls.weatherYears.setValue([]);
      this.hourlyDataRequestForm.controls.yearsStart.setValue(null);
      this.hourlyDataRequestForm.controls.yearsEnd.setValue(null);
    }
  }

  private updateReasonValidators(reasonType: HourlyDataReasonType | null): void {
    const specificReasonControl = this.hourlyDataRequestForm.controls.specificReason;
    const weatherYearsControl = this.hourlyDataRequestForm.controls.weatherYears;
    const yearsStartControl = this.hourlyDataRequestForm.controls.yearsStart;
    const yearsEndControl = this.hourlyDataRequestForm.controls.yearsEnd;

    if (reasonType === 'other') {
      specificReasonControl.clearValidators();
      if (this.isWeatherYearHidden()) {
        weatherYearsControl.clearValidators();
      } else {
        weatherYearsControl.setValidators(Validators.required);
      }
      yearsStartControl.setValidators(Validators.required);
      yearsEndControl.setValidators(Validators.required);
    } else {
      specificReasonControl.setValidators(Validators.required);
      weatherYearsControl.clearValidators();
      yearsStartControl.clearValidators();
      yearsEndControl.clearValidators();
    }
    specificReasonControl.updateValueAndValidity();
    weatherYearsControl.updateValueAndValidity();
    yearsStartControl.updateValueAndValidity();
    yearsEndControl.updateValueAndValidity();
  }

  ngOnInit(): void {
    const zone = this.regionsViewModel().regions.find(region => region.alias === this.defaultZone());
    const source = this.sourcesViewModel().sources.find(source => source.name === this.defaultSource());
    const basecase = this.basecasesViewModel().basecases.find(basecase => basecase.name === this.defaultBasecase());
    const weatherYear = this.weatherYearsViewModel().weatherYears.find(weatherYear => weatherYear.name === this.defaultWeatherYear());

    this.hourlyDataRequestForm.patchValue({
      zone: zone?.id,
      source: source?.id,
      basecase: basecase?.id,
      weatherYears: weatherYear ? [weatherYear.id] : [],
    });
  }

  isSpecificReason(): boolean {
    return this.hourlyDataRequestForm.value.reasonType === 'specific';
  }

  specificReasonLabel(reason: HourlyDataSpecificReason): string {
    return specificReasonLabels[reason];
  }

  removeSpecificReason(reason: HourlyDataSpecificReason, event: Event): void {
    event.stopPropagation();
    const specificReasonControl = this.hourlyDataRequestForm.controls.specificReason;
    specificReasonControl.setValue((specificReasonControl.value ?? []).filter(value => value !== reason));
  }

  weatherYearLabel(weatherYearId: number): string {
    return this.weatherYearsViewModel().weatherYears.find(weatherYear => weatherYear.id === weatherYearId)?.name ?? '';
  }

  removeWeatherYear(weatherYearId: number, event: Event): void {
    event.stopPropagation();
    const weatherYearsControl = this.hourlyDataRequestForm.controls.weatherYears;
    weatherYearsControl.setValue((weatherYearsControl.value ?? []).filter(value => value !== weatherYearId));
  }

  close(action?: string) {
    this.modal().closeModal(action);
  }

  save() {
    const formValue = this.hourlyDataRequestForm.value;
    const isOtherReason = formValue.reasonType === 'other';

    this.dispatch(
      requestHourlyData({
        email: this.connectedUser()?.preferred_username ?? '',
        zoneId: formValue.zone ?? 0,
        sourceId: formValue.source ?? 0,
        basecaseId: formValue.basecase ?? 0,
        comment: formValue.comment ?? '',
        weatherYearIds: isOtherReason ? (formValue.weatherYears ?? []) : [],
        reasons: formValue.reasonType === 'specific' ? (formValue.specificReason ?? []) : ['OTHER'],
        yearsStart: isOtherReason ? (formValue.yearsStart ?? null) : null,
        yearsEnd: isOtherReason ? (formValue.yearsEnd ?? null) : null,
      }),
    );

    this.close('save');
  }
}